import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('pb_token'));
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(() => {
    const saved = localStorage.getItem('pb_selected_product');
    return saved ? JSON.parse(saved) : null;
  });
  
  // Emulation state
  const [emulatedBy, setEmulatedBy] = useState(() => {
    const saved = localStorage.getItem('pb_emulated_by');
    return saved ? JSON.parse(saved) : null;
  });
  const [originalToken, setOriginalToken] = useState(() => localStorage.getItem('pb_original_token'));

  // Set up axios default header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error('Token verification failed:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };
    verifyToken();
  }, [token]);

  const login = async (username, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { username, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('pb_token', newToken);
    localStorage.removeItem('pb_selected_product');
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    setSelectedProduct(null);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('pb_token');
    localStorage.removeItem('pb_selected_product');
    localStorage.removeItem('pb_emulated_by');
    localStorage.removeItem('pb_original_token');
    setToken(null);
    setUser(null);
    setSelectedProduct(null);
    setEmulatedBy(null);
    setOriginalToken(null);
  };

  const selectProduct = (product) => {
    setSelectedProduct(product);
    if (product) {
      localStorage.setItem('pb_selected_product', JSON.stringify(product));
    } else {
      localStorage.removeItem('pb_selected_product');
    }
  };

  // Emulate a user (admin only)
  const emulateUser = async (userId) => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/emulate/${userId}`);
      const { token: newToken, user: emulatedUser, emulated_by } = response.data;
      
      // Save original admin token
      localStorage.setItem('pb_original_token', token);
      setOriginalToken(token);
      
      // Set emulation info
      localStorage.setItem('pb_emulated_by', JSON.stringify(emulated_by));
      setEmulatedBy(emulated_by);
      
      // Switch to emulated user token
      localStorage.setItem('pb_token', newToken);
      setToken(newToken);
      setUser(emulatedUser);
      
      // Clear selected product when emulating
      localStorage.removeItem('pb_selected_product');
      setSelectedProduct(null);
      
      return emulatedUser;
    } catch (error) {
      console.error('Emulation failed:', error);
      throw error;
    }
  };

  // Stop emulating and return to admin
  const stopEmulating = () => {
    if (originalToken) {
      localStorage.setItem('pb_token', originalToken);
      setToken(originalToken);
      
      // Clear emulation state
      localStorage.removeItem('pb_emulated_by');
      localStorage.removeItem('pb_original_token');
      setEmulatedBy(null);
      setOriginalToken(null);
      
      // Clear selected product
      localStorage.removeItem('pb_selected_product');
      setSelectedProduct(null);
      
      // Reload user data
      setLoading(true);
    }
  };

  // Refresh user data from the server
  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  const isAdmin = user?.role === 'admin' && !emulatedBy;
  const isProductManager = user?.role === 'product_manager';
  const canManageProduct = isAdmin || isProductManager;
  const isEmulating = !!emulatedBy;

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      logout,
      isAdmin,
      isProductManager,
      canManageProduct,
      selectedProduct,
      selectProduct,
      emulateUser,
      stopEmulating,
      isEmulating,
      emulatedBy,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
